import { ActionType } from 'core/rtbCampaign/singlePage/common';
import { OutBoundKeyword } from 'core/brand/Brand';
import { DefaultBrandManager } from 'core/brand/BrandManager';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useCampaignStore } from 'store/campaignStore';
import { useProductStore } from 'store/productStore';
import { Goal, OutboundKeywordInfo } from './useOutboundKeywordForm';
import { useCoreContext } from 'contexts/coreContext';
import { DefaultAdRequestSourceManager } from 'core/adRequestSource/AdRequestSourceManager';
import { DefaultRdpManager, RdpManager } from 'core/rdp/RdpManager';
import {
  SuggestKeywordProperty,
  SuggestKeywordType,
} from 'core/rtbCampaign/singlePage/keyword';
import { Product } from 'core/product/Product';
import _, { defaultTo } from 'lodash';
import { OutBound_default_AdContent } from '../Components/Keyword/Outbound/Outbound';
import { ProductFilter } from 'containers/RetailMomo/Products/ProductFilter';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';

interface OutboundProps {
  defaultForm: any;
  values: ReturnType<typeof useFormik>['values'];
  diffDays?: number;
  setFieldValue: ReturnType<typeof useFormik>['setFieldValue'];
  filters?: ProductFilter;
}

const defaultBrandManager = new DefaultBrandManager();
const defaultRdpManager: RdpManager = new DefaultRdpManager();
const adRequestSourceManager = new DefaultAdRequestSourceManager();

const useOutbound = (
  initialState: OutboundProps = {
    defaultForm: {},
    values: {},
    diffDays: 0,
    setFieldValue: async () => Promise.resolve(),
    filters: undefined,
  },
) => {
  const { defaultForm, values, setFieldValue, filters } = initialState;
  const core = useCoreContext();
  const { actionType, handleLoading } = useCampaignStore();
  const outsideMetaPermission: boolean = _.get(
    core,
    'accountManager.localeMeta.addonFeatures.campaign.outsideMeta',
    false,
  );

  const { selectedAutoProducts, changeSelectedAutoProducts } =
    useProductStore();

  // Meta 廣告投放帳號
  const [outboundBrandOptions, setOutboundBrandOptions] = useState<
    OutBoundKeyword[]
  >([]);

  // 設定站外資料(獨立寫是因為很多地方共用)
  const handleSetOutboundInfo = (outboundInfo: OutboundKeywordInfo) => {
    const { brandNo, brandName, name, logoUrl, adContent } = outboundInfo;
    setFieldValue('basic.outboundInfo.brandNo', brandNo);
    setFieldValue('basic.outboundInfo.brandName', brandName);
    setFieldValue('basic.outboundInfo.name', `${name} - momo購物網`);
    setFieldValue('basic.outboundInfo.logoUrl', logoUrl);
    setFieldValue('basic.outboundInfo.adContent', adContent);
    setFieldValue('basic.outboundInfo.segment', outboundInfo.segment || []);
  };
  // 清除站外資料
  const handleResetOutboundInfo = () => {
    if (!defaultForm?.basic?.outboundInfo?.brandNo) {
      handleSetOutboundInfo({
        brandNo: '',
        brandName: '',
        name: '',
        logoUrl: '',
        adContent: OutBound_default_AdContent,
        segment: [],
        goal: Goal.LINK_CLICKS,
      });
    }
  };

  const handleChangOutbound = (selectedOption: any) => {
    const selected = outboundBrandOptions?.find(
      (item) => item.value === selectedOption,
    );
    // 寫入站外資料
    setFieldValue('basic.outboundInfo.brandNo', selectedOption);
    setFieldValue('basic.outboundInfo.brandName', selected?.brandName || '');
    setFieldValue(
      'basic.outboundInfo.name',
      selected?.label ? `${selected.label} - momo購物網` : '',
    );
    setFieldValue('basic.outboundInfo.logoUrl', selected?.logoUrl || '');
    setFieldValue('basic.outboundInfo.adContent', OutBound_default_AdContent);
    setFieldValue(
      'basic.outboundInfo.segment',
      values?.basic?.outboundInfo?.segment || [],
    );
    setFieldValue(
      'basic.outboundInfo.goal',
      values?.basic?.outboundInfo?.goal || '',
    );
    // 當切換品牌時 trigger 啟動發送請求取得商品
    if (selected?.brandName) {
      trigger(selected.brandName);
    }
  };
  // 變更 Meta廣告主要文字
  const handleSetMetaContent = (text) => {
    setFieldValue('basic.outboundInfo.adContent', text);
  };

  // 取得站外品牌選項
  const { data: outboundBrandData, error: brandOptionsError } = useSWR(
    actionType === ActionType.CREATE || actionType === ActionType.COPY
      ? ['/outbound-keywords-brands']
      : null,
    () => defaultBrandManager.getOutBoundKeywordBrand(),

    {
      onSuccess: (data) => {
        setOutboundBrandOptions(data);
      },
      revalidateOnFocus: false, // 禁用視窗聚焦時的重新抓取
    },
  );

  // 根據已選擇的品牌取得商品
  const fetchBrandProducts = async (
    brandName: string | undefined | null,
  ): Promise<Product[]> => {
    if (!_.isEmpty(filters) && !_.isNil(filters.advertiser)) {
      // const advertiser = _.get(filters, 'advertiser', '');
      const retailId = _.get(filters, 'retailerId', '');
      if (_.isEmpty(retailId)) {
        return [];
      }
      try {
        const pageable = { page: 1, sizePerPage: 25 };
        const response = await defaultRdpManager.getRetailProducts(
          retailId,
          pageable,
          {
            // advertiser,
            // retailId,
            isOutBoundKeyword: true,
            brand: brandName,
          },
        );

        return response.products || [];
      } catch (error) {
        console.error('Failed to fetch products:', error);
        return [];
      }
    }
    return [];
  };
  // 根據是否有選取品牌來決定是否發送請求
  const {
    data: brandProducts,
    isMutating,
    trigger,
  } = useSWRMutation(
    [values?.basic?.outboundInfo?.brandName],
    async (_key, { arg }: { arg: string }) => fetchBrandProducts(arg),
    {
      onSuccess: (data) => {
        changeSelectedAutoProducts(() => data);
        if (!data || data.length === 0) {
          setFieldValue('limitations', {
            include: [
              {
                op: '',
                type: 'searchKeywords',
                value: [],
              },
            ],
          });
        }
      },
      onError: (error) => {
        console.error('Failed to fetch brand products:', error);
      },
    },
  );
  // 取得推薦關鍵字
  const fetchKeywords = async (products: Product[]) => {
    try {
      const smartGoodsHotKeywords =
        await adRequestSourceManager.getSmartGoodsHotKeywords(
          defaultTo(products, []).map((product) => product.productId),
        );

      if (smartGoodsHotKeywords.suggestKeywords.length === 0) {
        console.error('No suggestKeywords');
        setFieldValue('limitations', {
          include: [
            {
              op: '',
              type: 'searchKeywords',
              value: [],
            },
          ],
        });
        return [];
      }

      const suggestKeywords = smartGoodsHotKeywords.suggestKeywords.map(
        (item, i) => ({
          [SuggestKeywordProperty.SUGGEST_KEYWORD]: item.suggestKeyword,
          [SuggestKeywordProperty.PR]: item.pr?.toString(),
          [SuggestKeywordProperty.CPC]: item.cpc?.toString() || '',
          [SuggestKeywordProperty.STAR]:
            5 -
            Math.floor((5 * i) / smartGoodsHotKeywords.suggestKeywords.length),
        }),
      );

      setFieldValue('limitations', {
        include: [
          {
            op: '',
            type: 'searchKeywords',
            value: smartGoodsHotKeywords.suggestKeywords.map((keyword) => ({
              value: keyword.suggestKeyword,
              label: keyword.suggestKeyword,
              isError: false,
            })),
          },
        ],
      });

      return suggestKeywords;
    } catch (error) {
      console.error('Failed to fetch suggestKeywords:', error);

      setFieldValue('limitations', {
        include: [
          {
            op: '',
            type: 'searchKeywords',
            value: [],
          },
        ],
      });

      return [];
    }
  };
  // 僅在有選取商品時才發送請求
  const { data: fetchedSuggestKeywords } = useSWR<SuggestKeywordType[]>(
    selectedAutoProducts?.length && brandProducts?.length
      ? ['/outbound-keywords', selectedAutoProducts]
      : null,
    () => fetchKeywords(selectedAutoProducts),
    {
      revalidateOnFocus: false,
      onError: (error) => {
        console.error('Failed to fetch suggestKeywords:', error);
      },
    },
  );

  useEffect(() => {
    // 當為 create 模式或 selectBrand 改變時，重置站外資訊
    if (actionType === ActionType.CREATE) {
      handleResetOutboundInfo();
    }
  }, [actionType, values?.basic?.selectPattern]);

  useEffect(() => {
    handleLoading(isMutating);
  }, [isMutating, handleLoading]);

  return {
    outsideMetaPermission,
    handleSetMetaContent,
    handleChangOutbound,
    outboundBrandOptions: outboundBrandData || [],
    suggestKeywords: fetchedSuggestKeywords || [],
  };
};

export default useOutbound;
